import { useLayoutEffect, useEffect, useRef } from 'react';

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: string;
  ogImage?: string;
  schemaJsonLd?: Record<string, unknown>;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.help';

function upsertMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el: HTMLMetaElement | null = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el: HTMLLinkElement | null = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function useSeo(data: SeoData): void {
  const jsonLdId = useRef(`seo-jsonld-${Math.random().toString(36).slice(2, 9)}`);

  // Title: useLayoutEffect to avoid flash
  useLayoutEffect(() => {
    document.title = data.title;
  }, [data.title]);

  // Meta tags and JSON-LD
  useEffect(() => {
    const canonical = `${SITE_URL}${data.canonicalPath}`;

    upsertMeta('description', data.description);
    if (data.keywords) {
      upsertMeta('keywords', data.keywords);
    }
    upsertLink('canonical', canonical);
    upsertMeta('og:title', data.title, true);
    upsertMeta('og:description', data.description, true);
    upsertMeta('og:url', canonical, true);
    upsertMeta('og:type', data.ogType || 'article', true);
    if (data.ogImage) {
      upsertMeta('og:image', data.ogImage, true);
    }
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', data.title);
    upsertMeta('twitter:description', data.description);

    // Schema.org JSON-LD
    if (data.schemaJsonLd) {
      // Remove old JSON-LD from this hook instance
      const oldScript = document.getElementById(jsonLdId.current);
      if (oldScript) oldScript.remove();

      const schemaWithContext = {
        '@context': 'https://schema.org',
        ...data.schemaJsonLd,
      };

      const script = document.createElement('script');
      script.id = jsonLdId.current;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaWithContext);
      document.head.appendChild(script);
    }
  }, [data.title, data.description, data.keywords, data.canonicalPath, data.ogType, data.ogImage, data.schemaJsonLd]);
}